import { Optional } from "../../support/optional.model";
import { TypeSystem } from "../../support/type-system.support";
import { Material } from "../dto/material.model";

	/**
	 * @description
	 *	Resuelve dependencia circular originaria en el modelo Product.
	 */

	/**
	 * @description
	 *  Modelo de un producto.
	 *
	 * @see gigot-backend/Models/Products/Product.cs
	 */

export class Product {

	public active: boolean;
	public code: string;
	public id: number;
	public imagePath: string;
	public name: string;
	public productCategoryId: number;
	public productMaterials: ProductMaterial[];
	public campaignProductId: number;

	/**
	 * @description
	 *	Construye un producto con una semántica vacía. Nótese que no es lo
	 *  mismo que construir un producto con valores por defecto. Las reglas
	 *  generales de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Un producto vacío.
	 */
	public static empty(): Product {
		const product: Product = new Product();
		product.active = false;
		product.code = "";
		product.id = undefined;
		product.imagePath = "";
		product.name = "";
		product.productCategoryId = undefined;
		product.productMaterials = [];
		return product;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicProduct
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicProduct: Product): Product {
		if (!anemicProduct) return undefined;
		const product: Product = Product.empty();
		product.active = anemicProduct.active;
		product.code = anemicProduct.code;
		product.id = anemicProduct.id;
		product.imagePath = anemicProduct.imagePath;
		product.name = anemicProduct.name;
		product.productCategoryId = anemicProduct.productCategoryId;
		product.productMaterials = TypeSystem.orElse(anemicProduct.productMaterials, [])
			.map(productMaterial => ProductMaterial.from(productMaterial));
		return product;
	}

	/**
	 * @description
	 *	Indica la existencia de una ruta hacia la imagen de este producto,
	 *	útil para determinar la necesidad de mostrar una imagen por defecto en
	 *	la GUI.
	 *
	 * @returns
	 *	Verdadero si existe una imagen definida para este ítem, falso de otro
	 *	modo.
	 */
	public hasImage(): boolean {
		return Optional.of(this.imagePath)
			.filter(path => path.trim() !== "")
			.isPresent();
	}
}

	/**
	 * @description
	 *  Modelo del material de un producto.
	 *
	 * @see gigot-backend/Models/Products/Product.cs
	 */

export class ProductMaterial {

	public material: Material;
	public materialId: number;
	public product: Product;
	public productId: number;
	public quantity: number;

	/**
	 * @description
	 *	Construye un material de un producto con una semántica vacía. Nótese
	 *	que no es lo mismo que construir un material con valores por defecto.
	 *	Las reglas generales de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Un material de un producto vacío.
	 */
	public static empty(): ProductMaterial {
		const productMaterial: ProductMaterial = new ProductMaterial();
		productMaterial.material = Material.empty();
		productMaterial.materialId = undefined;
		productMaterial.product = Product.empty();
		productMaterial.productId = undefined;
		productMaterial.quantity = undefined;
		return productMaterial;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicProductMaterial
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicProductMaterial: ProductMaterial): ProductMaterial {
		if (!anemicProductMaterial) return undefined;
		const productMaterial: ProductMaterial = ProductMaterial.empty();
		productMaterial.material = Material.from(anemicProductMaterial.material);
		productMaterial.materialId = anemicProductMaterial.materialId;
		productMaterial.product = Product.from(anemicProductMaterial.product);
		productMaterial.productId = anemicProductMaterial.productId;
		productMaterial.quantity = anemicProductMaterial.quantity;
		return productMaterial;
	}
}
